import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { Separator } from 'app/design/primitives/text-divider';
import { IconMoodSmile, IconInfoCircle } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { ShowErrors } from 'app/components/show-errors';
import { useTranslation } from 'react-i18next';
import { useGuardianMutation } from 'app/network/guardian';
import type { Loan } from 'app/types/loan';
import type { Listing } from 'app/types/listing';
import { canAssignGuardian } from 'app/lib/guardian';
import { ActionRow } from 'app/screens/management/one-loan/components/action-row';
import { useUser } from 'app/network/user';
import { useIsFocused } from '@react-navigation/native';
import { useAtomValue } from 'jotai';
import { currentTutorialAtom } from 'app/atoms';
import { useTutorialRef, useTutorials } from 'app/components/tutorial-overlay';
import { Tutorial, TutorialRef } from 'app/types/tutorial';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

export { GuardianBadge, GUARDIAN_BLUE } from 'app/shopify/components/feedback/GuardianBadge';

export function GuardianConfirmation({
  open,
  onClose,
  onConfirm,
  pending,
  error,
  name,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: Error | null;
  name?: string;
}) {
  const { t } = useTranslation('guardian');
  return (
    <Modal open={open} onClose={pending ? undefined : onClose} transparent>
      <Column
        backgroundColor="surface.default"
        borderRadius="radius.xl"
        gap="spacing.general.x6"
        maxHeight="85%"
        maxWidth={343}
        padding="spacing.general.x4"
        width="95%"
      >
        <Modal.Title allowClose={!pending} onClose={onClose}>
          {t('confirmation')}
        </Modal.Title>
        <ScrollView>
          <Column gap="spacing.general.x4" paddingHorizontal="spacing.general.x7">
            <Text variant="bodyLargeStrong" fontSize={18} lineHeight={22}>
              {t(name ? 'confirmAssign' : 'confirmReclaim', { name })}
            </Text>
            {name ? (
              <Row
                backgroundColor="surface.tertiary-neutral"
                borderRadius="radius.sm"
                padding="spacing.general.x4"
                gap="spacing.general.x4"
              >
                <IconInfoCircle size={28} />
                <Text flex={1} fontSize={14}>
                  {t('endsLoan')}
                </Text>
              </Row>
            ) : null}
            <ShowErrors error={error} />
          </Column>
        </ScrollView>
        <Row gap="spacing.general.x2">
          <Button
            accessibilityRole="button"
            flex={1}
            minHeight={48}
            style={{ borderRadius: 24 }}
            variant="secondary"
            disabled={pending}
            onPress={onClose}
          >
            {t('cancel')}
          </Button>
          <Button
            accessibilityRole="button"
            flex={1}
            minHeight={48}
            style={{ borderRadius: 24 }}
            variant="primary"
            loading={pending}
            onPress={onConfirm}
          >
            {t('confirm')}
          </Button>
        </Row>
      </Column>
    </Modal>
  );
}

export function GuardianLoanAction({ loan, userId }: { loan?: Loan | null; userId?: string }) {
  const { t } = useTranslation('guardian');
  const [open, setOpen] = useState(false);
  const isFocused = useIsFocused();
  const currentTutorial = useAtomValue(currentTutorialAtom);
  const { viewRef } = useTutorialRef(TutorialRef.GuardianLoanAction);
  const { hasCompletedTutorial, startTutorial, completeTutorial } = useTutorials();
  const tutorialCompleted = hasCompletedTutorial(Tutorial.Guardian);
  const tutorialStartedForUser = useRef<string | undefined>(undefined);
  const mutation = useGuardianMutation();
  const eligible = mutation.canUseGuardian && Boolean(loan && canAssignGuardian(loan, userId));
  const startGuardianTutorial = useEffectEvent(() => {
    if (!mutation.canUseGuardian && currentTutorial === Tutorial.Guardian) {
      completeTutorial(false);
      return;
    }
    if (eligible && isFocused && !tutorialCompleted && !currentTutorial && tutorialStartedForUser.current !== userId) {
      tutorialStartedForUser.current = userId;
      startTutorial(Tutorial.Guardian);
    }
  });
  useEffect(() => {
    startGuardianTutorial();
  }, [eligible, isFocused, tutorialCompleted, currentTutorial, userId, mutation.canUseGuardian]);
  if (!eligible || !loan) return null;
  return (
    <Column gap="spacing.general.x4">
      <Separator onlyWidth />
      <View ref={viewRef} collapsable={false}>
        <ActionRow
          prefix={<IconMoodSmile strokeWidth={ICON_STROKE_WIDTH} />}
          text={t('action', { name: loan.borrower.profile?.firstName ?? '' })}
          onPress={() => {
            mutation.reset();
            setOpen(true);
          }}
        />
      </View>
      <GuardianConfirmation
        open={open}
        onClose={() => setOpen(false)}
        name={loan.borrower.profile?.firstName ?? t('member')}
        pending={mutation.isPending}
        error={mutation.error}
        onConfirm={() =>
          mutation.mutate(
            { loanId: loan.id },
            {
              onSuccess: () => {
                setOpen(false);
              },
            },
          )
        }
      />
    </Column>
  );
}

export function GuardianReclaimAction({ listing }: { listing: Listing }) {
  const { data: user } = useUser();
  const { t } = useTranslation('guardian');
  const [open, setOpen] = useState(false);
  const mutation = useGuardianMutation();
  if (!mutation.canUseGuardian || !listing.guardianId || user?.id !== listing.userId) return null;
  return (
    <>
      <Button
        accessibilityRole="button"
        minHeight={48}
        style={{ borderRadius: 100 }}
        variant="secondary"
        onPress={() => {
          mutation.reset();
          setOpen(true);
        }}
      >
        {t('reclaim')}
      </Button>
      <GuardianConfirmation
        open={open}
        onClose={() => setOpen(false)}
        pending={mutation.isPending}
        error={mutation.error}
        onConfirm={() => mutation.mutate({ listingId: listing.id }, { onSuccess: () => setOpen(false) })}
      />
    </>
  );
}
